/**
 * Which address sits right behind each entry of this tab's history, so a page's way back can tell that it leads to
 * the entry she came from — and go Back to it. Back brings that page as she left it (TanStack restores the scroll it
 * saved for that entry); pushing a fresh copy of the address opens it at the top.
 *
 * Fed from the router's `onResolved` (main.tsx). Entries are TanStack's: every navigation stamps one with a key and
 * an index. A push — or Forward — lands one index on, so the entry it came from is right behind it; a replace keeps
 * the index under a new key, so it inherits what was behind the entry it replaced. Kept in sessionStorage, so it
 * survives a reload (the browser keeps the entries' keys); a fresh tab starts empty.
 */
export interface TrailLocation {
  href: string;
  state: { __TSR_key?: string; key?: string; __TSR_index?: number };
}

type TrailStorage = Pick<Storage, 'getItem' | 'setItem'>;

const STORE_KEY = 'dw-history-trail';
/** Entries remembered (the oldest are forgotten first). */
const KEEP = 80;

const keyOf = (location: TrailLocation) => location.state.__TSR_key ?? location.state.key;

function sessionStore(): TrailStorage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function createHistoryTrail(storage: TrailStorage | null = sessionStore()) {
  let behind: Record<string, string> = {};
  try {
    const saved: unknown = JSON.parse(storage?.getItem(STORE_KEY) ?? '{}');
    if (saved && typeof saved === 'object') behind = Object.fromEntries(Object.entries(saved).filter((e): e is [string, string] => typeof e[1] === 'string'));
  } catch {
    // Unreadable: start empty.
  }
  const save = () => {
    const keys = Object.keys(behind);
    if (keys.length > KEEP) for (const key of keys.slice(0, keys.length - KEEP)) delete behind[key];
    try {
      storage?.setItem(STORE_KEY, JSON.stringify(behind));
    } catch {
      // Full or blocked: it still works for this page's life.
    }
  };
  return {
    /** One resolved navigation, from the router's `onResolved` event. */
    record(from: TrailLocation | undefined, to: TrailLocation) {
      const toKey = keyOf(to);
      const fromIndex = from?.state.__TSR_index;
      const toIndex = to.state.__TSR_index;
      if (!from || !toKey || fromIndex === undefined || toIndex === undefined) return;
      if (toIndex === fromIndex + 1) {
        behind[toKey] = from.href;
      } else if (toIndex === fromIndex) {
        const fromKey = keyOf(from);
        const prev = fromKey === undefined ? undefined : behind[fromKey];
        if (fromKey === toKey || prev === undefined) return;
        behind[toKey] = prev;
      } else {
        return;
      }
      save();
    },
    /** The address of the entry right behind `location`'s, when this tab saw her come from it. */
    behind(location: TrailLocation): string | undefined {
      const key = keyOf(location);
      return key === undefined ? undefined : behind[key];
    },
  };
}

/** The app's trail (one per tab). */
export const historyTrail = createHistoryTrail();

/** The same page address, whatever order its query parameters are written in. */
export function sameAddress(a: string, b: string): boolean {
  const norm = (href: string) => {
    try {
      const url = new URL(href, 'http://app.invalid');
      url.searchParams.sort();
      return `${url.pathname}?${url.searchParams.toString()}${url.hash}`;
    } catch {
      return href;
    }
  };
  return norm(a) === norm(b);
}
